<script setup lang="ts">
import { computed, toRef } from "vue";
import { useAnalysisQuery } from "@/composables/useAnalysisQuery";
import { resolveLocalizedTitle } from "@/composables/useCardData";
import { removeHtmlMarkdownAndLineBreak } from "@utils/toolBox";
import { Tooltip } from "@/components/ui/tooltip";
import type { AnalysisCount, ChartCard, TurfData } from "@/types";
import { cardCodes, isMultipleChoiceCard, shadeRamp, useAnalysisContext, useCardPopulation } from "../analysisData";
import { TURF_DEFAULT_COMBO_SIZE } from "../constants";
import AnalysisCardShell from "./AnalysisCardShell.vue";
import PopulationSwitch from "./PopulationSwitch.vue";

const props = defineProps({
  card: { type: Object, required: true },
  readonly: { type: Boolean, default: false },
});

const card = toRef(props, "card");
const { weighted, availableCards, displayLocale } = useAnalysisContext();
const { choices, selected, filters, color: populationColor } = useCardPopulation(card);

const questionId = computed<number | null>(() => props.card.content?.question_id || null);
const comboSize = computed<number>(() => props.card.content?.combo_size ?? TURF_DEFAULT_COMBO_SIZE);

const body = computed(() => {
  if (!questionId.value) return null;
  return {
    question_id: questionId.value,
    combo_size: comboSize.value,
    forced_codes: props.card.content?.forced_codes || [],
    excluded_codes: props.card.content?.excluded_codes || [],
    filters: filters.value,
  };
});

const { result, loading, state } = useAnalysisQuery<TurfData>("turf", body);

const count = (entry: AnalysisCount) => (weighted.value ? entry.weighted_nb : entry.nb);

const respondents = computed(() => {
  if (!result.value) return null;
  return weighted.value ? result.value.weighted_respondents : result.value.respondents;
});

const questionMeta = computed<ChartCard | null>(
  () =>
    (availableCards.value as ChartCard[]).find((c) => isMultipleChoiceCard(c) && (c.questions || []).some((q) => q.id === questionId.value)) ||
    null,
);

const resolveTitle = (title: unknown) => removeHtmlMarkdownAndLineBreak(resolveLocalizedTitle(title, displayLocale.value));

const itemTitle = computed(() => {
  const question = (questionMeta.value?.questions || []).find((q) => q.id === questionId.value);
  return question?.item_token ? resolveTitle(question.item_title) : null;
});

const labels = computed(() => cardCodes(questionMeta.value, resolveTitle));
const labelOf = (code: string) => labels.value.find((answer) => answer.code === code)?.label || code;

const accent = computed(() => props.card.options?.color || populationColor.value);

// The weighted base is rounded, the reaches are not.
const shareOf = (reach: number) => (respondents.value ? Math.min(100, (reach / respondents.value) * 100) : 0);

const steps = computed(() => {
  const curve = (weighted.value ? result.value?.weighted_curve : result.value?.curve) || [];
  const colors = shadeRamp(accent.value, curve.length);
  return curve.map((step, idx) => {
    const share = shareOf(count(step));
    return {
      code: step.code,
      label: labelOf(step.code),
      reach: Math.round(count(step)),
      share,
      added: share - (idx === 0 ? 0 : shareOf(count(curve[idx - 1]))),
      color: colors[idx],
    };
  });
});

const TOP_COMBOS = 10;

const combos = computed(() =>
  [...(result.value?.combos || [])]
    .sort((a, b) => count(b) - count(a))
    .slice(0, TOP_COMBOS)
    .map((combo) => ({
      key: combo.codes.join("."),
      labels: combo.codes.map(labelOf),
      reach: Math.round(count(combo)),
      share: shareOf(count(combo)),
    })),
);

const bestShare = computed(() => combos.value[0]?.share || 0);

const tooFewAnswers = computed(() => !!result.value && result.value.candidates < comboSize.value);
const truncated = computed(() => !!result.value && result.value.candidates < result.value.answers);

const cardState = computed(() => state((result.value?.respondents || 0) > 0));

const formatPercent = (value: number) => `${Math.round(value)}%`;
</script>

<template>
  <AnalysisCardShell
    :card="card"
    :readonly="readonly"
    :respondents="respondents"
    :weighted="weighted"
    respondents-hint="Respondents who answered the question, the base of every reach."
    description="The combination of answers that reaches the most distinct respondents (Total Unduplicated Reach). Bars count votes; this counts people, so two answers picked by the same respondents add votes but no reach. The build-up adds at each step the answer bringing the most new respondents; the best combinations come from an exhaustive search over the most chosen answers."
    :state="cardState"
    state-message="Select a multiple-choice question to find the combination with the widest reach.">
    <template #subtitle>
      <p v-if="questionMeta" class="text-xs text-muted-foreground">
        {{ questionMeta.name }}<template v-if="itemTitle"> · {{ itemTitle }}</template> · best {{ comboSize }} by unduplicated reach
      </p>
    </template>
    <template #toolbar>
      <PopulationSwitch v-if="choices.length > 1" :card="card" :populations="choices" :selected="selected" />
    </template>

    <div
      v-if="tooFewAnswers"
      class="rounded-md border border-status-warning-border bg-status-warning-soft px-3 py-2 text-xs leading-snug text-status-warning-strong">
      <span class="font-semibold">Not enough answers.</span>
      Only {{ result?.candidates }} {{ result?.candidates === 1 ? "answer was" : "answers were" }} chosen once the excluded ones are
      left out; a portfolio of {{ comboSize }} needs at least {{ comboSize }}.
    </div>
    <div v-else class="flex flex-col gap-4" :class="{ 'opacity-50': loading }">
      <div class="flex flex-col gap-1.5">
        <span class="heading-label text-foreground-subtle">Build-up, what each addition brings</span>
        <div
          v-for="(step, idx) in steps"
          :key="step.code"
          class="grid grid-cols-[minmax(0,11rem)_minmax(0,1fr)_3rem_3.5rem] items-center gap-x-2">
          <span class="truncate text-right text-xs text-muted-foreground" :title="step.label">{{ idx + 1 }}. {{ step.label }}</span>
          <div class="relative h-5 overflow-hidden rounded bg-muted">
            <div class="h-full rounded transition-all duration-300" :style="{ width: `${step.share}%`, backgroundColor: step.color }" />
          </div>
          <span class="text-right text-xs font-semibold tabular-nums">{{ formatPercent(step.share) }}</span>
          <span class="text-right text-2xs tabular-nums text-status-success-strong">+{{ Math.round(step.added) }} pts</span>
        </div>
      </div>

      <div class="flex flex-col gap-1">
        <span class="heading-label text-foreground-subtle">Best combinations</span>
        <div
          v-for="(combo, idx) in combos"
          :key="combo.key"
          class="flex items-center gap-2 rounded-md px-1 py-0.5"
          :class="{ 'bg-muted font-medium': idx === 0 }">
          <span class="w-5 shrink-0 text-xs tabular-nums text-muted-foreground">{{ idx + 1 }}</span>
          <Tooltip :delay-duration="150" content-class="max-w-xs" trigger-class="!cursor-default min-w-0 flex-1">
            <span class="block truncate text-xs">{{ combo.labels.join(" + ") }}</span>
            <template #content>
              <ul class="space-y-0.5">
                <li v-for="label in combo.labels" :key="label">· {{ label }}</li>
              </ul>
              <div class="mt-1 border-t border-white/20 pt-1 tabular-nums">{{ formatPercent(combo.share) }} of the base · {{ combo.reach }} respondents</div>
            </template>
          </Tooltip>
          <div class="hidden h-4 w-28 shrink-0 overflow-hidden rounded bg-muted sm:block">
            <div class="h-full rounded" :style="{ width: `${bestShare ? (combo.share / bestShare) * 100 : 0}%`, backgroundColor: accent }" />
          </div>
          <span class="w-16 shrink-0 text-right text-xs font-semibold tabular-nums">
            {{ formatPercent(combo.share) }} <span class="font-normal text-muted-foreground">({{ combo.reach }})</span>
          </span>
        </div>
      </div>

      <p v-if="truncated" class="text-2xs text-muted-foreground">
        Computed on the {{ result?.candidates }} most chosen answers of {{ result?.answers }}.
      </p>
    </div>
  </AnalysisCardShell>
</template>
